import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { recipes } from '../lib/api';
import RecipeThumbnail from '../components/RecipeThumbnail';

function Favorites() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['recipes', 'favorites'],
    queryFn: () => recipes.list({ favorite: true }),
  });

  const toggleFavoriteMutation = useMutation({
    mutationFn: (id) => recipes.toggleFavorite(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
    },
  });

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="max-w-7xl mx-auto animate-fadeIn">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Favorites</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {data?.pagination?.total || 0} favorite recipes
        </p>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg">
          {error.message || 'Failed to load favorites'}
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !error && data?.recipes?.length === 0 && (
        <div className="text-center py-16">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
            <svg className="w-8 h-8 text-yellow-500" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
              <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No favorites yet</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Star your favorite recipes to find them quickly here
          </p>
          <Link to="/recipes" className="btn btn-primary">
            Browse Recipes
          </Link>
        </div>
      )}

      {/* Recipe grid */}
      {!isLoading && !error && data?.recipes?.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {data.recipes.map((recipe) => (
            <Link
              key={recipe.id}
              to={`/recipe/${recipe.id}`}
              className="card overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Thumbnail */}
              <div className="aspect-video">
                <RecipeThumbnail recipe={recipe} />
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2">
                    {recipe.title}
                  </h3>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFavoriteMutation.mutate(recipe.id);
                    }}
                    className="flex-shrink-0 text-yellow-500"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2">
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  </button>
                </div>

                {recipe.sourceCreator && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {recipe.sourceCreator}
                  </p>
                )}

                <div className="flex items-center gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
                  {recipe.totalTime && <span>{recipe.totalTime}</span>}
                  {recipe.servings && <span>{recipe.servings}</span>}
                </div>

                <p className="text-xs text-gray-400 mt-3">{formatDate(recipe.createdAt)}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export default Favorites;
